import React, { useState, useEffect } from 'react';
import { PostPreviewModal } from '../components/OrganicPostModals';
import VipBadge from '../components/VipBadge';
import organicFetch from '../lib/organicFetch';
import { sessionHeaders } from '../services/auth';

const PROXY = 'https://crabplace-fb.netlify.app/.netlify/functions';
const ACCOUNT_ID = 'act_47263248';

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7d', label: 'Last 7 Days' },
  { value: 'last_14d', label: 'Last 14 Days' },
  { value: 'last_30d', label: 'Last 30 Days' },
  { value: 'last_90d', label: 'Last 90 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Map a Meta date_preset to a {from,to} YYYY-MM-DD range so our last-click numbers
// can be compared against Meta's reported numbers over (roughly) the same window.
// Approximate — Meta's exact preset boundaries vary — but close enough to eyeball.
function dateRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const minus = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  switch (preset) {
    case 'today': return { from: ymd(today), to: ymd(today) };
    case 'yesterday': { const y = minus(1); return { from: ymd(y), to: ymd(y) }; }
    case 'last_7d': return { from: ymd(minus(6)), to: ymd(today) };
    case 'last_14d': return { from: ymd(minus(13)), to: ymd(today) };
    case 'last_30d': return { from: ymd(minus(29)), to: ymd(today) };
    case 'last_90d': return { from: ymd(minus(89)), to: ymd(today) };
    case 'this_month': return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: ymd(today) };
    case 'last_month': return { from: ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: ymd(new Date(now.getFullYear(), now.getMonth(), 0)) };
    default: return { from: null, to: null };
  }
}

function fmt$(n) {
  if (n == null) return '—';
  return '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtN(n) {
  if (n == null) return '—';
  return parseFloat(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function fmtPct(n) {
  if (n == null) return '—';
  return parseFloat(n).toFixed(2) + '%';
}

function StatusBadge({ status }) {
  const active = status === 'ACTIVE';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
      background: active ? '#e6f9f0' : '#f5f5f5',
      color: active ? '#1a8a4a' : 'var(--gray-500)',
      textTransform: 'uppercase', letterSpacing: '0.5px'
    }}>
      {status}
    </span>
  );
}

const NEON = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/neon'
  : '/.netlify/functions/neon';

function slugName(s) {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function neonQuery(query, params = {}) {
  return fetch(NEON, { method: 'POST', headers: { 'Content-Type': 'application/json', ...sessionHeaders() }, body: JSON.stringify({ query, params }) })
    .then(r => r.json()).then(d => d.rows || []);
}

// Side-by-side: Meta's reported purchases (Ads Manager — includes view-through /
// assisted) vs our true last-click count. The gap is Meta over-attribution.
function MetaVsTracked({ metaPurchases, metaRevenue, tracked, preset }) {
  const meta = parseInt(metaPurchases || 0, 10);
  const ours = parseInt((tracked && tracked.total_attributed) || 0, 10);
  const gap = Math.max(0, meta - ours);
  const pct = meta > 0 ? Math.round((gap / meta) * 100) : 0;
  const presetLabel = (DATE_PRESETS.find(p => p.value === preset) || {}).label || preset;
  const cell = { flex: 1, padding: '14px 16px', textAlign: 'center' };
  const big = { fontSize: 28, fontWeight: 800, lineHeight: 1.1, fontFamily: 'Montserrat' };
  const lab = { fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 };
  const sub = { fontSize: 10.5, color: 'var(--gray-500)', marginTop: 4, lineHeight: 1.35 };
  return (
    <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ background: 'var(--gray-50)', padding: '8px 14px', fontSize: 12, fontWeight: 700, color: 'var(--navy)', borderBottom: '1px solid var(--gray-200)' }}>
        Meta says vs. we tracked · {presetLabel}
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <div style={cell}>
          <div style={{ ...lab, color: '#1877f2' }}>Meta reports</div>
          <div style={{ ...big, color: '#1877f2' }}>{meta.toLocaleString()}</div>
          <div style={sub}>purchases · {fmt$(metaRevenue)}<br />Ads Manager — counts view-through &amp; assisted</div>
        </div>
        <div style={{ flex: '0 0 44px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-500)', fontSize: 22 }}>›</div>
        <div style={{ ...cell, borderLeft: '1px solid var(--gray-100)', borderRight: '1px solid var(--gray-100)' }}>
          <div style={{ ...lab, color: '#1a8a4a' }}>We tracked (last-click)</div>
          <div style={{ ...big, color: '#1a8a4a' }}>{ours.toLocaleString()}</div>
          <div style={sub}>direct ad-click orders · {fmt$(tracked && tracked.total_revenue)}<br />The honest number</div>
        </div>
        <div style={cell}>
          <div style={{ ...lab, color: 'var(--orange)' }}>Over-attribution gap</div>
          <div style={{ ...big, color: 'var(--orange)' }}>{gap.toLocaleString()}{meta > 0 ? <span style={{ fontSize: 14, fontWeight: 700 }}> ({pct}%)</span> : null}</div>
          <div style={sub}>sales Meta claims that didn't come from an ad click</div>
        </div>
      </div>
      <div style={{ padding: '7px 14px', fontSize: 10.5, color: 'var(--gray-500)', borderTop: '1px solid var(--gray-100)', background: '#fcfcfd' }}>
        Windows are approximate (Meta's preset boundaries differ slightly). A gap is normal — it's the view-through / assisted credit Meta takes for sales other channels actually closed.
      </div>
    </div>
  );
}

// The ads inside one paid campaign — thumbnail, copy, engagement, and a
// "Preview & comments" button that opens the same PostPreviewModal the
// Organic Performance tab uses (every ad is backed by a real Page post).
function CampaignAdsModal({ data, onPreview, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 640, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>Ads in this campaign</div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 2, maxWidth: 480 }}>{data.campaignName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: 'var(--gray-500)' }}>×</button>
        </div>
        <div style={{ padding: 14, maxHeight: '72vh', overflowY: 'auto' }}>
          {data.loading && <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 8 }}>Loading ads…</div>}
          {!data.loading && data.error && <div style={{ fontSize: 12, color: '#c33', padding: 8 }}>{data.error}</div>}
          {!data.loading && !data.error && data.ads.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 8 }}>No ads found in this campaign.</div>
          )}
          {!data.loading && data.ads.map((ad) => (
            <div key={ad.id} style={{ display: 'flex', gap: 12, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 12, marginBottom: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: '0 0 92px' }}>
                {ad.thumb
                  ? <img src={ad.thumb} alt="" style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 6, background: 'var(--gray-100)' }} />
                  : <div style={{ width: 92, height: 92, borderRadius: 6, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--gray-500)' }}>no image</div>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>{ad.name}</span>
                  <StatusBadge status={ad.status} />
                </div>
                {ad.headline && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginTop: 4 }}>{ad.headline}</div>}
                {ad.message && <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{ad.message}</div>}
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span>👍 {ad.likes.toLocaleString()}</span>
                  <span>💬 {ad.comments.toLocaleString()}</span>
                  {ad.shares > 0 && <span>↗ {ad.shares.toLocaleString()} shares</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  {ad.storyId && (
                    <button onClick={() => onPreview(ad)}
                      style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', fontFamily: 'Montserrat', padding: '5px 12px' }}>
                      Preview & comments{ad.comments > 0 ? ` (${ad.comments})` : ''}
                    </button>
                  )}
                  {ad.permalink && <a href={ad.permalink} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 600, color: 'var(--orange)', alignSelf: 'center' }}>Open on Facebook ↗</a>}
                  {ad.linkUrl && <a href={ad.linkUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', alignSelf: 'center' }} title={ad.linkUrl}>Landing page ↗</a>}
                </div>
              </div>
            </div>
          ))}
          {!data.loading && !data.error && (
            <div style={{ fontSize: 10.5, color: 'var(--gray-400)', lineHeight: 1.5, paddingTop: 4 }}>
              Every ad is backed by a real Facebook Page post, so preview, comments, hide, and reply work exactly like the Organic Posts performance view. Replies post as the Page.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Shared attributed-orders table (used for both Paid and Organic Facebook).
function AttrOrdersTable({ rows, navigateTo }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order</th>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
            <th style={{ textAlign: 'center', padding: '8px', fontWeight: 700, color: 'var(--gray-500)' }} title="Is this the customer's first-ever order? Paid campaigns exclude your existing list, so Yes = a genuinely new buyer.">New?</th>
            <th style={{ textAlign: 'center', padding: '8px', fontWeight: 700, color: 'var(--gray-500)' }} title="Is this customer an active VIP Club member right now?">VIP?</th>
            <th style={{ textAlign: 'right', padding: '8px', fontWeight: 700, color: 'var(--gray-500)' }}>Amount</th>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 700, color: 'var(--gray-500)' }}>Source</th>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 700, color: 'var(--gray-500)' }}>Medium</th>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 700, color: 'var(--gray-500)' }}>Campaign</th>
            <th style={{ textAlign: 'center', padding: '8px', fontWeight: 700, color: 'var(--gray-500)' }}>fbclid</th>
            <th style={{ textAlign: 'center', padding: '8px', fontWeight: 700, color: 'var(--gray-500)' }}>gclid</th>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a, i) => {
            const canceled = a.canceled === '1' || a.canceled === 1 || a.odeleted === '1' || a.odeleted === 1;
            return (
            <tr key={a.id} style={{ borderBottom: '1px solid var(--gray-100)', background: canceled ? '#fbecec' : (i % 2 === 0 ? '#fff' : 'var(--gray-50)'), cursor: 'pointer', opacity: canceled ? 0.8 : 1 }}
              onClick={() => navigateTo?.('order-details', a.bc_order_id)}>
              <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600, textDecoration: canceled ? 'line-through' : 'none' }}>#{a.bc_order_id}</td>
              <td style={{ padding: '8px', textDecoration: canceled ? 'line-through' : 'none' }}>{[a.ofirstname, a.olastname].filter(Boolean).join(' ') || '—'}</td>
              <td style={{ padding: '8px', textAlign: 'center' }}>
                {a.new_customer === true ? <span style={{ color: 'var(--green, #1a8a4a)', fontWeight: 700, fontSize: 11 }}>Yes</span>
                  : a.new_customer === false ? <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>No</span>
                  : '—'}
              </td>
              <td style={{ padding: '8px', textAlign: 'center' }}>
                {a.is_vip === true
                  ? <VipBadge notRenewing={a.vip_not_renewing === true} style={{ fontSize: 9, background: 'var(--orange)', color: 'white', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }} />
                  : a.is_vip === false ? <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>No</span>
                  : '—'}
              </td>
              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                {canceled
                  ? <span style={{ background: '#f8d7da', color: '#a11', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>CANCELLED · $0</span>
                  : (a.orderamount ? fmt$(a.orderamount) : '—')}
              </td>
              <td style={{ padding: '8px' }}>
                {a.utm_source && <span style={{ background: '#e8f4fd', color: '#1a6fb0', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{a.utm_source}</span>}
              </td>
              <td style={{ padding: '8px', fontSize: 11 }}>{a.utm_medium || '—'}</td>
              <td style={{ padding: '8px', fontSize: 11, maxWidth: 180 }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.utm_campaign || '—'}</div>
              </td>
              <td style={{ padding: '8px', textAlign: 'center' }}>
                {a.fbclid ? <span style={{ color: 'var(--green, #1a8a4a)', fontWeight: 700, fontSize: 11 }}>Yes</span> : '—'}
              </td>
              <td style={{ padding: '8px', textAlign: 'center' }}>
                {a.gclid ? <span style={{ color: 'var(--green, #1a8a4a)', fontWeight: 700, fontSize: 11 }}>Yes</span> : '—'}
              </td>
              <td style={{ padding: '8px 12px', fontSize: 11 }}>{a.created_at ? new Date(a.created_at).toLocaleDateString('en-US') : '—'}</td>
            </tr>
          );})}
        </tbody>
      </table>
    </div>
  );
}

export default function MetaCampaigns({ navigateTo, embedded }) {
  const [datePreset, setDatePreset] = useState('last_30d');
  const [campaigns, setCampaigns] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('campaigns'); // 'campaigns' | 'attribution'
  const [attrOrders, setAttrOrders] = useState([]);
  const [attrSummary, setAttrSummary] = useState(null);
  const [organicOrders, setOrganicOrders] = useState([]);
  const [organicSummary, setOrganicSummary] = useState(null);
  const [attrLoading, setAttrLoading] = useState(false);
  const [funnel, setFunnel] = useState({});   // utm_campaign -> { carts, checkouts, signups } (first-party, same source as Organic)
  const [adsView, setAdsView] = useState(null);   // { campaignName, loading, ads, error } — the ads-in-campaign modal
  const [adPreview, setAdPreview] = useState(null); // post-shaped object for PostPreviewModal
  const [ordersPopup, setOrdersPopup] = useState(null); // { title, loading, rows, error } — "Our Orders" drill-down
  const [crossOrders, setCrossOrders] = useState([]); // Meta-touched orders whose last click was email/SMS/organic (credit stolen)
  const [metaDeltas, setMetaDeltas] = useState([]); // value-delta poller: "Meta's likely orders"
  const [storeTotals, setStoreTotals] = useState(null); // { order_count, revenue } — ALL store orders in window (Reality Check)

  // Click a campaign row -> jump STRAIGHT into the first ad's preview/comments
  // (same modal as Organic Posts). Multi-ad campaigns get pill tabs inside the
  // modal; the ads-list modal only shows when nothing is previewable.
  function openCampaignAds(c) {
    setAdsView({ campaignName: c.name, loading: true, ads: [], error: null });
    organicFetch({
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'campaign-ads', campaignId: c.id }),
    })
      .then(r => r.json())
      .then(d => {
        const ads = d.ads || [];
        setAdsView(v => v && { ...v, loading: false, ads, error: d.error || null });
        const firstPreviewable = ads.find(a => a.storyId);
        if (firstPreviewable) previewAd(firstPreviewable);
      })
      .catch(e => setAdsView(v => v && { ...v, loading: false, error: e.message }));
  }

  // Map an ad onto the shape PostPreviewModal expects (it's a Page post).
  function previewAd(ad) {
    setAdPreview({
      adId: ad.id,
      fbPostId: ad.storyId,
      permalink: ad.permalink,
      linkUrl: ad.linkUrl,
      brand: 'crabplace',
      likes: ad.likes,
      comments: ad.comments,
      shares: ad.shares,
      caption: ad.message || ad.headline || ad.name,
      thumb: ad.thumb,
      orders: 0,
    });
  }

  // "Our Orders" drill-down: show the actual order numbers behind the count — for one
  // campaign (pass the campaign) or all (pass null). Lazy-fetches attribution_list so it
  // works on the Campaigns tab without loading the full Attributed-Orders view.
  async function showOurOrders(campaign) {
    const slug = campaign ? slugName(campaign.name) : null;
    const title = campaign ? `Our Orders · ${campaign.name}` : 'Our Orders · all campaigns';
    setOrdersPopup({ title, loading: true, rows: [] });
    const { from, to } = dateRange(datePreset);
    try {
      const orders = await neonQuery('attribution_list', { channel: 'meta', from, to, limit: 500, offset: 0 });
      const all = Array.isArray(orders) ? orders : [];
      const rows = slug ? all.filter(o => slugName(o.utm_campaign || '') === slug) : all;
      setOrdersPopup({ title, loading: false, rows });
    } catch (e) {
      setOrdersPopup({ title, loading: false, rows: [], error: e.message });
    }
  }

  function exportOrdersCsv(rows, title) {
    const header = ['Order', 'Customer', 'New Customer', 'VIP', 'Amount', 'Source', 'Medium', 'Campaign', 'fbclid', 'gclid', 'Date'];
    const lines = rows.map(a => [
      a.bc_order_id,
      [a.ofirstname, a.olastname].filter(Boolean).join(' '),
      a.new_customer === true ? 'Yes' : a.new_customer === false ? 'No' : '',
      a.is_vip === true ? (a.vip_not_renewing === true ? 'Yes - not renewing' : 'Yes') : a.is_vip === false ? 'No' : '',
      a.orderamount || '',
      a.utm_source || '', a.utm_medium || '', a.utm_campaign || '',
      a.fbclid ? 'yes' : '', a.gclid ? 'yes' : '',
      a.created_at ? new Date(a.created_at).toISOString().slice(0, 10) : '',
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `our-orders-${title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    setCampaigns(null);
    setInsights(null);

    const fr = dateRange(datePreset);
    neonQuery('paid_funnel_by_campaign', { from: fr.from, to: fr.to })
      .then((rows) => {
        const m = {};
        // Key by the SAME normalization used at lookup time (funnel[slugName(campaign.name)]).
        // Previously keyed by the raw utm_campaign, so a match only happened by luck —
        // any casing/punctuation drift between the stored tag and the Meta campaign name
        // silently zeroed out "Our Orders". Slugifying both sides makes it reliable.
        (Array.isArray(rows) ? rows : []).forEach((r) => { m[slugName(r.utm_campaign)] = r; });
        setFunnel(m);
      })
      .catch(() => setFunnel({}));

    // Cross-attributed Meta clicks: orders carrying a Facebook/Instagram click-ID
    // whose LAST click was email/SMS/organic, so the strict paid count above never
    // credits them. This is the visibility into "orders that aren't getting credit."
    neonQuery('attribution_list', { channel: 'meta_organic', from: fr.from, to: fr.to, limit: 500, offset: 0 })
      .then((rows) => setCrossOrders(Array.isArray(rows) ? rows : []))
      .catch(() => setCrossOrders([]));

    // "Meta's likely orders" — the value-delta poller's recent increments + candidate matches.
    neonQuery('meta_value_deltas_list', { days: 7 })
      .then((rows) => setMetaDeltas(Array.isArray(rows) ? rows : []))
      .catch(() => setMetaDeltas([]));

    // Total store orders + revenue in the same window (all sources) — the Reality Check
    // denominator: Meta can't have driven more orders than the store actually received.
    if (fr.from && fr.to) {
      neonQuery('orders_totals_in_range', { from: fr.from, to: fr.to })
        .then((rows) => setStoreTotals(Array.isArray(rows) ? rows[0] : rows))
        .catch(() => setStoreTotals(null));
    } else {
      setStoreTotals(null);
    }

    const rangeQs = fr.from && fr.to ? `&since=${fr.from}&until=${fr.to}` : '';
    fetch(`${PROXY}/campaigns?account_id=${ACCOUNT_ID}&date_preset=${datePreset}${rangeQs}&status=ALL`)
      .then(r => r.json())
      .then(campData => {
        if (campData?.error) throw new Error(campData.error.message || JSON.stringify(campData.error));
        if (campData?.success === false) throw new Error(campData?.message || 'Proxy returned success=false');
        // proxy wraps: { success, data: { data: [...campaigns] } }
        const list = campData?.data?.data ?? campData?.data ?? campData ?? [];
        const arr = Array.isArray(list) ? list : [];
        if (arr.length === 0) console.warn('[MetaCampaigns] empty campaign list — raw response:', campData);
        setCampaigns(arr);
        // insights are embedded in each campaign as campaign.insights.data[0]
        const insightMap = {};
        arr.forEach(c => {
          const ins = c.insights?.data?.[0];
          if (ins) insightMap[c.id] = ins;
        });
        setInsights(insightMap);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [datePreset]);

  // Fetch attribution data when tab switches
  useEffect(() => {
    if (tab !== 'attribution') return;
    setAttrLoading(true);
    const { from, to } = dateRange(datePreset);
    Promise.all([
      neonQuery('attribution_list', { channel: 'meta', from, to, limit: 200, offset: 0 }),
      neonQuery('attribution_summary', { channel: 'meta', from, to }),
      neonQuery('attribution_list', { channel: 'meta_organic', from, to, limit: 200, offset: 0 }),
      neonQuery('attribution_summary', { channel: 'meta_organic', from, to }),
    ]).then(([orders, summary, orgOrders, orgSummary]) => {
      setAttrOrders(orders);
      setAttrSummary(summary[0] || null);
      setOrganicOrders(orgOrders);
      setOrganicSummary(orgSummary[0] || null);
    }).catch(() => {})
      .finally(() => setAttrLoading(false));
  }, [tab, datePreset]);

  const campList = Array.isArray(campaigns) ? campaigns : [];

  function getPurchases(ins) {
    return parseInt(ins.actions?.find(a => a.action_type === 'purchase')?.value || 0);
  }
  function getRevenue(ins) {
    return parseFloat(ins.action_values?.find(a => a.action_type === 'purchase')?.value || 0);
  }

  // totals
  const totals = campList.reduce((acc, c) => {
    const ins = insights?.[c.id] || {};
    acc.spend += parseFloat(ins.spend || 0);
    acc.clicks += parseInt(ins.clicks || 0);
    acc.impressions += parseInt(ins.impressions || 0);
    acc.purchases += getPurchases(ins);
    acc.revenue += getRevenue(ins);
    return acc;
  }, { spend: 0, clicks: 0, impressions: 0, purchases: 0, revenue: 0 });

  // First-party totals (our own click-attributed orders + real revenue).
  // `cancelled` is tracked separately so a cancelled order never inflates the
  // live count/revenue but is still visible (shown struck-through in the drill-down).
  const fpTotals = Object.values(funnel).reduce(
    (acc, r) => ({
      orders: acc.orders + (parseInt(r.orders, 10) || 0),
      revenue: acc.revenue + (parseFloat(r.revenue) || 0),
      cancelled: acc.cancelled + (parseInt(r.cancelled, 10) || 0),
    }),
    { orders: 0, revenue: 0, cancelled: 0 }
  );

  // Cross-attributed Meta-touched orders (Facebook/Instagram click-ID present, but a
  // later email/SMS/organic touch took last-click credit). Live = exclude cancelled.
  const crossLive = crossOrders.filter((o) => o.canceled !== '1' && o.canceled !== 1 && o.odeleted !== '1' && o.odeleted !== 1);
  const crossTotals = crossLive.reduce(
    (acc, o) => ({ orders: acc.orders + 1, revenue: acc.revenue + (parseFloat(o.orderamount) || 0) }),
    { orders: 0, revenue: 0 }
  );
  // Full Meta-influenced picture: paid last-click + cross-attributed.
  const metaInfluenced = { orders: fpTotals.orders + crossTotals.orders, revenue: fpTotals.revenue + crossTotals.revenue };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>{embedded ? 'Campaign Performance' : 'Meta Campaigns'}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--gray-200)' }}>
            {[{ key: 'campaigns', label: 'Campaigns' }, { key: 'attribution', label: 'Attributed Orders' }].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'Montserrat', border: 'none', cursor: 'pointer',
                  background: tab === t.key ? 'var(--navy)' : '#fff', color: tab === t.key ? '#fff' : 'var(--navy)' }}>
                {t.label}
              </button>
            ))}
          </div>
        <select
          value={datePreset}
          onChange={e => setDatePreset(e.target.value)}
          style={{ fontSize: 12, padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: 6, color: 'var(--navy)', fontFamily: 'Montserrat', fontWeight: 600, cursor: 'pointer' }}
        >
          {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        </div>
      </div>

      {tab === 'campaigns' && <>
      {/* Summary KPIs */}
      {!loading && !error && insights && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi-card">
            <div className="kpi-label">Total Spend</div>
            <div className="kpi-value">{fmt$(totals.spend)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Clicks</div>
            <div className="kpi-value">{totals.clicks.toLocaleString()}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Impressions</div>
            <div className="kpi-value">{totals.impressions.toLocaleString()}</div>
          </div>
          <div className="kpi-card" title="Meta-reported purchases (includes view-through & assisted)">
            <div className="kpi-label">Meta Purchases</div>
            <div className="kpi-value">{totals.purchases.toLocaleString()}</div>
          </div>
          <div className="kpi-card" title="Live orders our own tracking tied to an ad click (utm match) + their real order totals. Cancelled orders are excluded from this count and shown struck-through in the drill-down. Click to see the order numbers."
            style={{ cursor: (fpTotals.orders > 0 || fpTotals.cancelled > 0) ? 'pointer' : 'default' }}
            onClick={() => (fpTotals.orders > 0 || fpTotals.cancelled > 0) && showOurOrders(null)}>
            <div className="kpi-label">Our Orders / Rev {(fpTotals.orders > 0 || fpTotals.cancelled > 0) && <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--navy)' }}>· view</span>}</div>
            <div className="kpi-value">{fpTotals.orders.toLocaleString()} · {fmt$(fpTotals.revenue)}</div>
            {fpTotals.cancelled > 0 && <div style={{ fontSize: 10.5, fontWeight: 600, color: '#a11', marginTop: 2 }}>{fpTotals.cancelled} cancelled (excluded)</div>}
          </div>
        </div>
      )}

      {/* Reality Check — Meta's self-reported claim vs. numbers it can't game */}
      {(() => {
        const metaClaimed = Number(totals.purchases) || 0;
        const actualOrders = storeTotals ? Number(storeTotals.order_count) : null;
        const pct = actualOrders && actualOrders > 0 ? (metaClaimed / actualOrders) * 100 : null;
        const spend = Number(totals.spend) || 0;
        const mer = spend > 0 && storeTotals ? Number(storeTotals.revenue) / spend : null;
        const pctColor = pct == null ? 'var(--gray-500)' : pct > 40 ? 'var(--red, #c0392b)' : pct > 20 ? '#b45309' : 'var(--green, #1a8a4a)';
        const verdict = pct == null ? '' :
          pct > 100 ? 'Meta claims MORE orders than your whole store received — pure over-claim.' :
          pct > 40 ? 'Implausibly high for paid social — Meta is likely taking credit for orders you would have gotten anyway.' :
          pct > 20 ? 'On the high side — sanity-check with a holdout test before scaling spend.' :
          'Within a believable range for paid social.';
        return (
          <div className="card" style={{ marginBottom: 16, border: '1px solid #f0c8b0', background: '#fffaf6' }}>
            <div className="card-header">Reality Check — is Meta&rsquo;s number real?</div>
            <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600 }}>Meta&rsquo;s claim vs. your actual orders</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: pctColor, marginTop: 2 }}>{pct != null ? `${Math.round(pct)}%` : '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-600)', marginTop: 2 }}>
                  Meta claims <strong>{metaClaimed.toLocaleString()}</strong> of your <strong>{actualOrders != null ? actualOrders.toLocaleString() : '—'}</strong> total store orders this window
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600 }}>Verified (click-traced) orders</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', marginTop: 2 }}>{(Number(fpTotals.orders) || 0).toLocaleString()}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-600)', marginTop: 2 }}>The honest floor — orders we can tie to a real ad click</div>
              </div>
              <div style={{ cursor: crossOrders.length > 0 ? 'pointer' : 'default' }}
                onClick={() => crossOrders.length > 0 && setOrdersPopup({ title: 'Cross-attributed Meta clicks — FB/IG click, credit taken by a later email/SMS (incl. cancelled)', loading: false, rows: crossOrders })}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600 }}>Cross-attributed Meta clicks {crossOrders.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy)' }}>· view all</span>}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#b5651d', marginTop: 2 }}>{crossTotals.orders.toLocaleString()} · {fmt$(crossTotals.revenue)}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-600)', marginTop: 2 }}>
                  FB/IG click, but email/SMS took the credit{(crossOrders.length - crossLive.length) > 0 ? ` · ${crossOrders.length - crossLive.length} cancelled` : ''} — click for order #s
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600 }}>Blended MER</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', marginTop: 2 }}>{mer != null ? `${mer.toFixed(2)}x` : '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-600)', marginTop: 2 }}>Total store revenue &divide; Meta spend (Meta spend only — Google not yet included)</div>
              </div>
            </div>
            {(metaInfluenced.orders > 0) && (
              <div style={{ padding: '0 16px 12px', fontSize: 12, color: 'var(--navy)' }}>
                <strong>Total Meta-influenced (paid + cross-attributed): {metaInfluenced.orders.toLocaleString()} orders · {fmt$(metaInfluenced.revenue)}</strong>
                <span style={{ color: 'var(--gray-500)', fontWeight: 500 }}> — the fuller floor once you count every order that had a Meta ad click in its path, not just last-click paid.</span>
              </div>
            )}
            {verdict && <div style={{ padding: '0 16px 12px', fontSize: 11.5, color: pctColor, fontWeight: 600 }}>{verdict}</div>}
            <div style={{ padding: '0 16px 14px', fontSize: 10.5, color: 'var(--gray-500)', lineHeight: 1.5 }}>
              Meta grades its own homework — view-through, modeled, and cross-device conversions inflate its count, and it won&rsquo;t hand back the order IDs. This panel checks Meta against numbers it can&rsquo;t game: your real total orders, click-verified orders, and blended return. The only true test of whether Meta <em>caused</em> orders is a holdout — turn it off in some regions and see if sales actually drop.
            </div>
          </div>
        );
      })()}

      {/* Meta's likely orders — value-delta matcher */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          Meta&rsquo;s likely orders — matched by value{' '}
          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)' }}>
            (Meta won&rsquo;t hand back order IDs; we watch each value increment and match the exact $ to candidate orders — a shortlist, not certain)
          </span>
        </div>
        {metaDeltas.length === 0 ? (
          <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--gray-600)', lineHeight: 1.55 }}>
            Nothing to shortlist right now. This matcher watches Meta&rsquo;s reported purchase <em>value</em> climb and matches the exact dollar amount to a real order &mdash; but Meta has been sending purchase <em>counts</em> with no dollar values (the same reason the &ldquo;Meta Rev&rdquo; column is blank), so there&rsquo;s nothing to line up against an order. The poller runs every 10 minutes; likely orders will appear here on their own once Meta starts reporting purchase values again. (Checked the last 7 days: no value increments.)
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Meta added</th>
                  <th style={{ textAlign: 'left', padding: '8px', fontWeight: 700, color: 'var(--gray-500)' }}>Detected</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Likely order(s)</th>
                </tr>
              </thead>
              <tbody>
                {metaDeltas.map((d) => {
                  const cands = Array.isArray(d.candidates) ? d.candidates : [];
                  return (
                    <tr key={d.id} style={{ borderBottom: '1px solid var(--gray-100)', verticalAlign: 'top' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--navy)', whiteSpace: 'nowrap' }}>
                        {fmt$(d.delta_value)}
                        {d.delta_count > 1 && <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)' }}> (+{d.delta_count} count)</span>}
                        <div style={{ fontSize: 10, color: 'var(--gray-500)', fontWeight: 500 }}>{(d.campaign_name || '').slice(0, 26)}</div>
                      </td>
                      <td style={{ padding: '8px', fontSize: 11, color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>
                        {d.detected_at ? new Date(d.detected_at).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {cands.length === 0 ? (
                          <span style={{ color: 'var(--gray-500)', fontStyle: 'italic' }}>no exact-value match (may be multiple orders, or a value we don&rsquo;t have)</span>
                        ) : (
                          cands.map((c, i) => (
                            <div key={c.order} style={{ marginBottom: 2, fontWeight: i === 0 ? 700 : 400 }}>
                              <span onClick={() => navigateTo?.('order-details', c.order)} style={{ color: 'var(--navy)', textDecoration: 'underline', cursor: 'pointer' }}>#{c.order}</span>{' '}
                              {c.name || ''}{c.loc ? ` · ${c.loc}` : ''}
                              {c.newCustomer && <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, color: 'var(--green, #1a8a4a)' }}>NEW</span>}
                              {c.inGeo && <span style={{ marginLeft: 4, fontSize: 9.5, fontWeight: 700, color: '#b5651d' }}>IN-ZONE</span>}
                              {i === 0 && cands.length > 1 && <span style={{ marginLeft: 6, fontSize: 9.5, color: 'var(--gray-500)' }}>best match</span>}
                            </div>
                          ))
                        )}
                        {cands.length > 1 && <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{cands.length} orders share this exact total — top one ranked by in-zone + new customer</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Campaign Table */}
      <div className="card">
        <div className="card-header">Campaigns</div>
        {loading && <div style={{ padding: '16px', fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: '16px', fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && campList.length === 0 && (
          <div style={{ padding: '16px', fontSize: 12, color: 'var(--gray-500)' }}>No campaigns found.</div>
        )}
        {!loading && !error && campList.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Campaign</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Spend</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Impressions</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Clicks</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>CTR</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>CPC</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }} title="Distinct sessions that added to cart (our storefront tracking, matched by this campaign's UTM tag)">Carts</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }} title="Distinct sessions that reached checkout">Checkout</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }} title="Newsletter / SMS signups">Signups</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }} title="Orders captured by our own tracking — the shopper clicked this ad and completed an order (utm_campaign match). The honest, first-party count.">Our Orders</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }} title="Revenue of those first-party attributed orders (real order totals from our database)">Our Rev</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }} title="Meta-reported purchases (pixel + CAPI, includes view-through & assisted — usually higher than Our Orders)">Meta Orders</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }} title="Meta-reported purchase value. Blank since 6/19 — Meta is receiving purchase counts without dollar values (see Our Rev for real revenue)">Meta Rev</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }} title="Meta-reported return on ad spend (needs Meta Rev)">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {campList.map((c, i) => {
                  const ins = insights?.[c.id] || {};
                  const roas = ins.purchase_roas?.[0]?.value ?? ins.roas ?? null;
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600, maxWidth: 260, cursor: 'pointer' }}
                        title="Click to see this campaign's ads — preview, comments, reply"
                        onClick={() => openCampaignAds(c)}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--gray-500)', fontWeight: 400 }}>{c.objective}</div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}><StatusBadge status={c.status} /></td>
                      <td style={{ padding: '8px', textAlign: 'right', color: 'var(--navy)', fontWeight: 600 }}>{fmt$(ins.spend)}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{ins.impressions ? parseInt(ins.impressions).toLocaleString() : '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{ins.clicks ? parseInt(ins.clicks).toLocaleString() : '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{fmtPct(ins.ctr)}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{fmt$(ins.cpc)}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{(funnel[slugName(c.name)]?.carts ?? 0) > 0 ? funnel[slugName(c.name)].carts.toLocaleString() : '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{(funnel[slugName(c.name)]?.checkouts ?? 0) > 0 ? funnel[slugName(c.name)].checkouts.toLocaleString() : '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{(funnel[slugName(c.name)]?.signups ?? 0) > 0 ? funnel[slugName(c.name)].signups.toLocaleString() : '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: 'var(--navy)', cursor: ((funnel[slugName(c.name)]?.orders ?? 0) > 0 || (funnel[slugName(c.name)]?.cancelled ?? 0) > 0) ? 'pointer' : 'default' }}
                        title={((funnel[slugName(c.name)]?.orders ?? 0) > 0 || (funnel[slugName(c.name)]?.cancelled ?? 0) > 0) ? 'Click to see the order number(s)' : undefined}
                        onClick={(e) => { if ((funnel[slugName(c.name)]?.orders ?? 0) > 0 || (funnel[slugName(c.name)]?.cancelled ?? 0) > 0) { e.stopPropagation(); showOurOrders(c); } }}>
                        <span style={{ fontWeight: (funnel[slugName(c.name)]?.orders ?? 0) > 0 ? 700 : 400, textDecoration: (funnel[slugName(c.name)]?.orders ?? 0) > 0 ? 'underline' : 'none' }}>{(funnel[slugName(c.name)]?.orders ?? 0) > 0 ? funnel[slugName(c.name)].orders.toLocaleString() : '—'}</span>
                        {(funnel[slugName(c.name)]?.cancelled ?? 0) > 0 && <span style={{ display: 'block', fontSize: 9.5, fontWeight: 600, color: '#a11' }}>+{funnel[slugName(c.name)].cancelled} cancelled</span>}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: Number(funnel[slugName(c.name)]?.revenue ?? 0) > 0 ? 700 : 400, color: 'var(--navy)' }}>{Number(funnel[slugName(c.name)]?.revenue ?? 0) > 0 ? fmt$(funnel[slugName(c.name)].revenue) : '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{getPurchases(ins) > 0 ? getPurchases(ins).toLocaleString() : '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: getRevenue(ins) > 0 ? 600 : 400 }}>{getRevenue(ins) > 0 ? fmt$(getRevenue(ins)) : '—'}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: roas >= 1 ? 'var(--green, #1a8a4a)' : roas != null ? 'var(--red, #c0392b)' : 'inherit', fontWeight: roas != null ? 700 : 400 }}>
                        {roas != null ? fmtN(roas) + 'x' : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>}

      {/* Attribution Tab */}
      {tab === 'attribution' && (
        <>
          <MetaVsTracked
            metaPurchases={totals.purchases}
            metaRevenue={totals.revenue}
            tracked={attrSummary}
            preset={datePreset}
          />
          {attrSummary && !attrLoading && (
            <div className="kpi-grid" style={{ marginBottom: 16 }}>
              <div className="kpi-card">
                <div className="kpi-label">Attributed Orders</div>
                <div className="kpi-value">{parseInt(attrSummary.total_attributed || 0).toLocaleString()}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Revenue</div>
                <div className="kpi-value">{fmt$(attrSummary.total_revenue)}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Campaigns</div>
                <div className="kpi-value">{parseInt(attrSummary.unique_campaigns || 0)}</div>
              </div>
            </div>
          )}
          <div className="card">
            <div className="card-header">Attributed Orders · Paid ads</div>
            {attrLoading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
            {!attrLoading && attrOrders.length === 0 && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No <strong>paid Meta ad</strong> orders in this window. This list shows only orders from a paid ad click (utm_medium=paid_social from the Campaign Builder, or a tagged Meta campaign). Organic Facebook clicks are broken out below; Google and email/SMS show on their own pages.</div>
            )}
            {!attrLoading && attrOrders.length > 0 && <AttrOrdersTable rows={attrOrders} navigateTo={navigateTo} />}
          </div>

          {/* Organic Facebook — fbclid clicks with no paid UTMs (page website link,
              shares, organic posts). Kept separate so they don't inflate paid. */}
          <div className="card">
            <div className="card-header">Organic Facebook · not paid</div>
            <div style={{ padding: '10px 16px', fontSize: 11.5, color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-100)' }}>
              Orders from a Facebook click that <strong>wasn't</strong> a paid ad — a plain crabplace.com link (your page's website link, a share, a comment) or an organic post. Facebook stamps an fbclid on every outbound link, so these carry an fbclid but no paid campaign. Counted here, never in the paid number above.
            </div>
            {!attrLoading && organicSummary && (
              <div className="kpi-grid" style={{ padding: 12 }}>
                <div className="kpi-card"><div className="kpi-label">Organic FB Orders</div><div className="kpi-value">{parseInt(organicSummary.total_attributed || 0).toLocaleString()}</div></div>
                <div className="kpi-card"><div className="kpi-label">Revenue</div><div className="kpi-value">{fmt$(organicSummary.total_revenue)}</div></div>
              </div>
            )}
            {attrLoading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
            {!attrLoading && organicOrders.length === 0 && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No organic Facebook orders in this window.</div>
            )}
            {!attrLoading && organicOrders.length > 0 && <AttrOrdersTable rows={organicOrders} navigateTo={navigateTo} />}
          </div>
        </>
      )}

      {adsView && !adPreview && (
        <CampaignAdsModal data={adsView} onPreview={previewAd} onClose={() => setAdsView(null)} />
      )}
      {adPreview && (
        <PostPreviewModal
          key={adPreview.fbPostId || adPreview.adId}
          post={adPreview}
          adTabs={(() => {
            const previewable = ((adsView && adsView.ads) || []).filter(a => a.storyId);
            return previewable.length > 1
              ? { ads: previewable, activeId: adPreview.adId, onSelect: previewAd }
              : null;
          })()}
          onClose={() => { setAdPreview(null); setAdsView(null); }}
        />
      )}

      {ordersPopup && (
        <div onClick={() => setOrdersPopup(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: 'min(820px, 96vw)', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--gray-200)', position: 'sticky', top: 0, background: '#fff' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>{ordersPopup.title}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {ordersPopup.rows.length > 0 && (
                  <button onClick={() => exportOrdersCsv(ordersPopup.rows, ordersPopup.title)}
                    style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--navy)', background: 'var(--gray-100)', border: '1px solid var(--gray-300)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                    Export CSV
                  </button>
                )}
                <button onClick={() => setOrdersPopup(null)}
                  style={{ fontSize: 20, lineHeight: 1, color: 'var(--gray-500)', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
              </div>
            </div>
            <div style={{ padding: 16 }}>
              {ordersPopup.loading ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 12.5 }}>Loading orders…</div>
              ) : ordersPopup.error ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--red, #c0392b)', fontSize: 12.5 }}>Error: {ordersPopup.error}</div>
              ) : ordersPopup.rows.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 12.5, lineHeight: 1.6 }}>
                  No attributed order rows found yet for this selection.<br />
                  Attribution rebuilds on a ~15-minute cron after an order is placed — if you just tested, give it a few minutes and reopen.
                </div>
              ) : (
                <AttrOrdersTable rows={ordersPopup.rows} navigateTo={navigateTo} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
